import { cleanParamValue, readAttributionParams, readOriginSignals } from "./tracking";

/** Origens de campanha conhecidas (LPs pagas / verticais). */
export const CAMPAIGN_SOURCES = new Set(["google", "instagram", "tiktok", "clinicas", "chatgpt"]);

/**
 * Chave de origem da sessão (sessionStorage): vale apenas durante o acesso.
 * Se o visitante sair e voltar depois por outra página, a origem é recalculada.
 */
const SESSION_TOUCH_KEY = "kentro_session_source_v1";

/** Classifica um domínio de referer externo em uma origem legível. */
export function classifyReferrerDomain(domain: string | undefined): string | undefined {
  if (!domain) return undefined;
  const d = domain.toLowerCase();
  if (/google\./.test(d)) return "organic_google";
  if (/bing\./.test(d)) return "organic_bing";
  if (/duckduckgo\./.test(d)) return "organic_duckduckgo";
  if (/yahoo\./.test(d)) return "organic_yahoo";
  if (/instagram\./.test(d) || /facebook\./.test(d) || /fb\.com$/.test(d)) return "instagram";
  if (/tiktok\./.test(d)) return "tiktok";
  if (/linkedin\./.test(d) || /lnkd\.in$/.test(d)) return "linkedin";
  if (/youtube\.|youtu\.be$/.test(d)) return "youtube";
  if (/chatgpt\.|openai\.|perplexity\.|gemini\./.test(d)) return "ai_search";
  if (/whatsapp\.|wa\.me$/.test(d)) return "whatsapp";
  if (/linktr\.ee$/.test(d)) return "linktree";
  return `referral_${d}`;
}

/** Lê a origem salva na sessão atual (expira quando o acesso termina). */
export function readFirstTouch(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(SESSION_TOUCH_KEY);
    return raw || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Salva a origem apenas na sessão (last-touch dentro do acesso).
 * Um novo acesso começa sem valor e recalcula a origem normalmente.
 */
export function persistFirstTouch(source: string) {
  if (typeof window === "undefined") return;
  if (!source || source === "direct" || source === "unknown") return;
  try {
    window.sessionStorage.setItem(SESSION_TOUCH_KEY, source);
    // Limpa resíduo de versões anteriores que persistiam por 30 dias.
    window.localStorage.removeItem("kentro_first_source_v1");
  } catch {
    /* noop */
  }
}


/** Normaliza valores de utm_source / path / referer para uma origem de campanha conhecida. */
export function normalizeSource(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.toLowerCase();
  if (CAMPAIGN_SOURCES.has(v)) return v;
  if (v.includes("google") || v.includes("gads") || v.includes("adwords")) return "google";
  if (v.includes("instagram") || v === "ig" || v.includes("facebook") || v === "fb" || v.includes("meta"))
    return "instagram";
  if (v.includes("tiktok") || v === "tt") return "tiktok";
  if (v.includes("clinica")) return "clinicas";
  if (v.includes("chatgpt") || v.includes("openai") || v === "oai") return "chatgpt";
  return undefined;
}

/**
 * Converte um path interno na origem do lead:
 * "/" -> home, "/ia" -> ia, "/clinicas/odontologicas" -> clinicas,
 * "/funcionalidades/crm" -> funcionalidades, "/en/ia" -> ia.
 */
export function sourceFromPath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const segments = path.split("/").filter(Boolean);
  if (segments[0] === "en" || segments[0] === "es") segments.shift();
  const first = segments[0];
  if (!first) return "home";
  if (first === "contato") return undefined; // não é origem
  return normalizeSource(first) ?? first.toLowerCase();
}

/** Subdomínios que servem a home e devem ser distinguidos na atribuição. */
export const HOST_SOURCES: Record<string, string> = {
  api: "api",
  chat: "chat",
  indicacao: "indicacao",
  automacao: "automacao",
  kentrobot: "kentrobot",
};

/**
 * Converte o host de entrada em origem quando o visitante chegou por um
 * subdomínio que renderiza a home (ex.: api.kentro.com.br → "api").
 */
export function sourceFromHost(host: string | undefined): string | undefined {
  if (!host) return undefined;
  const h = host.toLowerCase().replace(/^www\./, "");
  const first = h.split(".")[0];
  return HOST_SOURCES[first];
}

export type ResolvedOrigin = {
  source: string;
  isCampaign: boolean;
  /** Como a origem foi determinada — útil para diagnosticar atribuição. */
  attribution_method: string;
  origin_page: string;
  previous_page: string;
  landing_path: string;
  referrer_domain: string;
};

/**
 * Resolve a origem do lead em cascata:
 * 1) ?src= da URL  2) sessionStorage (persistido nas LPs)
 * 3) UTMs / click IDs  4) página anterior / LP de entrada da sessão
 * 5) referer externo classificado  6) origem salva na sessão atual
 * 7) "direct" (fallback consistente)
 */
export function resolveOrigin(urlSrc?: string | undefined): ResolvedOrigin {
  const signals =
    typeof window === "undefined"
      ? { origin_page: "", previous_page: "", landing_path: "", referrer_domain: "", landing_host: "" }
      : readOriginSignals();

  let campaign = normalizeSource(urlSrc);
  let method = campaign ? "url_src" : "";

  if (!campaign && typeof window !== "undefined") {
    try {
      campaign = normalizeSource(sessionStorage.getItem("kentro_campaign_source"));
      if (campaign) method = "session_campaign";
    } catch {
      /* noop */
    }
  }

  if (!campaign && typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      // Fallback para UTMs/click IDs persistidos na sessão (navegação interna
      // perde os parâmetros da URL, mas a atribuição precisa sobreviver).
      const stored = readAttributionParams();
      const get = (k: string) =>
        cleanParamValue(params.get(k)) || (stored as Record<string, string>)[k] || null;
      campaign =
        normalizeSource(get("utm_source")) ??
        normalizeSource(get("utm_campaign")) ??
        normalizeSource(get("utm_medium")) ??
        normalizeSource(get("gclid") || get("wbraid") || get("gbraid") ? "google" : null) ??
        normalizeSource(get("fbclid") ? "instagram" : null) ??
        normalizeSource(get("ttclid") ? "tiktok" : null) ??
        normalizeSource(get("oppref") || get("ad_account_id") ? "chatgpt" : null);
      if (campaign) method = "utm_or_clickid";
    } catch {
      /* noop */
    }
  }

  if (campaign) {
    try {
      sessionStorage.setItem("kentro_campaign_source", campaign);
    } catch {
      /* noop */
    }
    persistFirstTouch(campaign);
    return { source: campaign, isCampaign: true, attribution_method: method, ...signals };
  }

  // Origem interna: página anterior ao form, senão LP de entrada da sessão.
  let internal =
    sourceFromPath(signals.previous_page) ??
    sourceFromPath(signals.origin_page) ??
    sourceFromPath(signals.landing_path);

  // Subdomínios (api/chat/...) renderizam a home: sem isto ficariam como "home".
  const hostSource = sourceFromHost(signals.landing_host);
  let fromHost = false;
  if (hostSource && (!internal || internal === "home")) {
    internal = hostSource;
    fromHost = true;
  }

  const referral = classifyReferrerDomain(signals.referrer_domain);
  const firstTouch = readFirstTouch();

  let source = internal ?? referral ?? firstTouch;
  if (internal) method = fromHost ? "subdomain_host" : "internal_page";
  else if (referral) method = "referrer";
  else if (firstTouch) method = "session_touch";

  if (!source) {
    source = "direct";
    method = "direct_fallback";
  }

  persistFirstTouch(source);
  return { source, isCampaign: CAMPAIGN_SOURCES.has(source), attribution_method: method, ...signals };
}
